/**
 * PATH: packages/drafting/src/panels/LayerPanel.tsx
 * FILE_ID: DRAFTING-PANEL-LAYER-001
 * PURPOSE: Layer list — pick the active draw layer, toggle visibility.
 *   The colour swatches are the legend the markup prototype taught us
 *   users expect (Concrete/Aluminium/Glazing/...).
 */
import { Eye, EyeOff } from 'lucide-react'
import type { Layer } from '../ir/schema'

export interface LayerPanelProps {
  layers: Layer[]
  activeLayerId: string
  onActivate: (id: string) => void
  onToggleVisible: (id: string) => void
}

export function LayerPanel(props: LayerPanelProps) {
  const { layers, activeLayerId, onActivate, onToggleVisible } = props
  return (
    <section className="space-y-1">
      <h3 className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Layers</h3>
      <ul className="space-y-0.5">
        {layers.map((l) => {
          const active = l.id === activeLayerId
          const swatch = l.color && l.color !== 'none' ? l.color : l.stroke ?? '#94a3b8'
          return (
            <li key={l.id}>
              <div
                className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
                  active ? 'bg-rose-50 text-rose-900 dark:bg-rose-950/50 dark:text-rose-100' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                <button
                  type="button"
                  onClick={() => onActivate(l.id)}
                  className="flex flex-1 items-center gap-2 text-left"
                  aria-pressed={active}
                >
                  <span
                    className="h-3.5 w-3.5 rounded-sm border border-slate-300 dark:border-slate-600"
                    style={{ background: swatch }}
                    aria-hidden
                  />
                  <span className="truncate">{l.name}</span>
                </button>
                <button
                  type="button"
                  title={l.visible === false ? 'Show layer' : 'Hide layer'}
                  aria-label={l.visible === false ? 'Show layer' : 'Hide layer'}
                  onClick={() => onToggleVisible(l.id)}
                  className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                >
                  {l.visible === false ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
                </button>
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
